import { useState, useEffect } from 'react';
import { useAuth, api } from '@/App';
import { useI18n } from '@/i18n';
import { useBranding } from '@/contexts/BrandingContext';
import { HeroSection } from '@/components/landing/HeroSection';
import { FeaturedProperties } from '@/components/landing/FeaturedProperties';
import { LandingFooter } from '@/components/landing/LandingFooter';
import { LandingNavbar } from '@/components/landing/LandingNavbar';
import {
  FeaturesSection,
  DestinationsSection,
  CouponBanner,
  LastMinuteSection,
  TestimonialsSection,
  CtaSection,
  NewsletterSection,
} from '@/components/landing/LandingSections';
import { InstagramSection } from '@/components/landing/InstagramSection';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';

export default function LandingPage() {
  const { login, isAuthenticated } = useAuth();
  const { t, locale, formatPrice } = useI18n();
  const { branding: brandData } = useBranding();

  const bt = (brandField, i18nKey, params) => {
    if (locale === 'es' && brandField) return brandField;
    return t(i18nKey, params);
  };

  const [featuredProperties, setFeaturedProperties] = useState([]);
  const [stats, setStats] = useState({ properties: 0, cities: 0, countries: 0 });
  const [countryStats, setCountryStats] = useState({});
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [topReviews, setTopReviews] = useState([]);
  const [propertyRatings, setPropertyRatings] = useState({});
  const [favoriteIds, setFavoriteIds] = useState(new Set());
  const [activeCoupons, setActiveCoupons] = useState([]);
  const [lastMinuteOffers, setLastMinuteOffers] = useState([]);
  const [instagramFeed, setInstagramFeed] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const response = await api.get('/properties?limit=3&status=Publicado');
        const props = response.data.properties || [];
        setFeaturedProperties(props);
        if (props.length > 0) {
          const ids = props.map(p => p.property_id).join(',');
          const ratingsRes = await api.get(`/reviews/ratings-batch?property_ids=${ids}`);
          setPropertyRatings(ratingsRes.data.ratings || {});
        }
      } catch (err) { logger.error('Failed to load featured properties:', err.message); }
      try {
        const response = await api.get('/properties/stats/by-country');
        const data = response.data;
        setStats({ properties: data.total || 0, cities: 15, countries: data.country_count || 0 });
        const cMap = {};
        (data.countries || []).forEach(c => { cMap[c.country] = c.count; });
        setCountryStats(cMap);
      } catch (err) { logger.error('Failed to load stats:', err.message); }
      try {
        const response = await api.get('/reviews/top?limit=4');
        setTopReviews(response.data.reviews || []);
      } catch (err) { logger.error('Failed to load reviews:', err.message); }
      try {
        const response = await api.get('/coupons/active');
        setActiveCoupons(response.data.coupons || []);
      } catch (err) { logger.error('Failed to load coupons:', err.message); }
      try {
        const response = await api.get('/properties/last-minute');
        setLastMinuteOffers(response.data.properties || []);
      } catch (err) { logger.error('Failed to load last-minute offers:', err.message); }
      try {
        const response = await api.get('/instagram-feed?limit=3&sort=engagement');
        setInstagramFeed(response.data);
      } catch (err) { logger.error('Failed to load Instagram feed:', err.message); }
    };
    loadData();
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    const loadFavorites = async () => {
      try {
        const response = await api.get('/favorites/ids');
        setFavoriteIds(new Set(response.data.property_ids || []));
      } catch (err) { logger.error('Failed to load favorites:', err.message); }
    };
    loadFavorites();
  }, [isAuthenticated]);

  const toggleFavorite = async (e, propertyId) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isAuthenticated) {
      toast.info('Inicia sesión para guardar favoritos');
      return;
    }
    try {
      const res = await api.post(`/favorites/${propertyId}`);
      setFavoriteIds(prev => {
        const next = new Set(prev);
        if (res.data.action === 'added') next.add(propertyId);
        else next.delete(propertyId);
        return next;
      });
      toast.success(res.data.action === 'added' ? 'Agregado a favoritos' : 'Eliminado de favoritos');
    } catch (error) {
      toast.error('Error al actualizar favoritos');
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F4F0]">
      <LandingNavbar mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />

      <HeroSection
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        brandData={brandData}
        stats={stats}
      />

      <CouponBanner coupons={activeCoupons} t={t} />

      <FeaturesSection brandData={brandData} locale={locale} bt={bt} t={t} stats={stats} />

      <DestinationsSection brandData={brandData} locale={locale} bt={bt} t={t} countryStats={countryStats} />

      <FeaturedProperties
        properties={featuredProperties}
        propertyRatings={propertyRatings}
        favoriteIds={favoriteIds}
        onToggleFavorite={toggleFavorite}
        brandData={brandData}
      />

      <LastMinuteSection offers={lastMinuteOffers} t={t} formatPrice={formatPrice} />

      <TestimonialsSection reviews={topReviews} brandData={brandData} bt={bt} t={t} />

      <CtaSection brandData={brandData} bt={bt} />

      <InstagramSection feed={instagramFeed} brandData={brandData} t={t} />

      <NewsletterSection t={t} api={api} />

      <LandingFooter brandData={brandData} t={t} />
    </div>
  );
}
